import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Loader2, Plus, X } from "lucide-react";
import CompanyLogo from "@/components/shared/CompanyLogo";
import { useSearchCompaniesLazyQuery } from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { isLovablePreview } from "@/lib/utils/environment";
import { companies as allCompanies } from "@/data/dummy";
import AddCompanyDialog from "@/components/shared/AddCompanyDialog";
import { useCanMutate } from "@/lib/rbac";
import type { AssociationRelationship } from "@/lib/utils/company-associations-store";

/**
 * The four relationship values, and the catalog key each renders through.
 * The VALUE is stored on the association and must not change; only the label
 * is translated (written out as full literal keys so the locale audit's usage
 * scan can see them).
 */
const RELATIONSHIPS: AssociationRelationship[] = ["parent", "subsidiary", "partner", "other"];
const RELATIONSHIP_KEYS: Record<string, string> = {
  parent: "grid.association.parent",
  subsidiary: "grid.association.subsidiary",
  partner: "grid.association.partner",
  other: "grid.association.other",
};

interface CompanyPill {
  id: string;
  name: string;
  domain?: string | null;
  logoUrl?: string | null;
  relationship?: AssociationRelationship;
}

interface AssociatedCompaniesProps {
  companies: CompanyPill[];
  onChange: (companies: CompanyPill[]) => void;
  excludeId?: string; // Exclude the current company from search results
  onCompanyClick?: (company: CompanyPill) => void;
}

export default function AssociatedCompanies({
  companies: associatedCompanies,
  onChange,
  excludeId,
  onCompanyClick,
}: AssociatedCompaniesProps) {
  const { t } = useTranslation();
  const canMutate = useCanMutate();
  const relationshipLabel = (rel: string) => {
    const key = RELATIONSHIP_KEYS[rel];
    return key ? t(key) : rel;
  };
  const [searching, setSearchingState] = useState(false);
  const setSearching = (next: boolean) => {
    if (next && !canMutate) return;
    setSearchingState(next);
  };
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState<CompanyPill[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  // When a search result (or newly created company) is picked, hold it here
  // and prompt for the relationship type before committing to the list.
  const [pendingCompany, setPendingCompany] = useState<CompanyPill | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const workspaceId = useWorkspaceId();

  const [searchCompanies, { loading }] = useSearchCompaniesLazyQuery();

  useEffect(() => {
    if (searching) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [searching]);

  // Close on outside click
  useEffect(() => {
    if (!searching && !pendingCompany) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      // If the click target has already been detached from the DOM (because a
      // React handler on this component just unmounted it — e.g. picking a
      // search result collapses the results list), it's not a true outside
      // click. `contains` would report false against the detached node and
      // incorrectly close the picker.
      if (target && !(target as any).isConnected) return;
      if (containerRef.current && target && !containerRef.current.contains(target)) {
        setSearching(false);
        setSearchText("");
        setResults([]);
        setPendingCompany(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searching, pendingCompany]);

  const fetchCompanies = useCallback(async (search: string) => {
    if (isLovablePreview()) {
      const filtered = allCompanies
        .filter(c =>
          c.name.toLowerCase().includes(search.toLowerCase()) &&
          c.id !== excludeId &&
          !associatedCompanies.some(ac => ac.id === c.id)
        )
        .slice(0, 8)
        .map(c => ({
          id: c.id,
          name: c.name,
          domain: c.website?.replace(/^https?:\/\//, ''),
          logoUrl: null,
        }));
      setResults(filtered);
      return;
    }

    if (!search.trim()) {
      setResults([]);
      return;
    }

    try {
      const result = await searchCompanies({
        variables: {
          workspaceId: String(workspaceId),
          search: search.trim(),
          first: 10,
        },
      });

      const accounts = result.data?.accounts?.edges
        ?.map(edge => edge?.node)
        .filter(Boolean)
        .filter(a => a.id !== excludeId && !associatedCompanies.some(ac => ac.id === a.id))
        .map(account => ({
          id: account.id,
          name: account.properties?.name || account.domain || 'Unknown',
          domain: account.domain,
          logoUrl: (account.properties as any)?.logoUrl,
        })) || [];

      setResults(accounts);
    } catch (error) {
      console.error('Error searching companies:', error);
      setResults([]);
    }
  }, [workspaceId, searchCompanies, excludeId, associatedCompanies]);

  // Debounced search
  useEffect(() => {
    if (!searchText.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => fetchCompanies(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText, fetchCompanies]);

  const pickCompany = (company: CompanyPill) => {
    setPendingCompany(company);
    setSearchText("");
    setResults([]);
  };

  const confirmRelationship = (relationship: AssociationRelationship) => {
    if (!pendingCompany) return;
    onChange([...associatedCompanies, { ...pendingCompany, relationship }]);
    setPendingCompany(null);
    setSearching(false);
  };

  const removeCompany = (id: string) => {
    onChange(associatedCompanies.filter(c => c.id !== id));
  };

  const handleCreateNew = () => {
    setShowAddDialog(true);
    setSearching(false);
    setSearchText("");
    setResults([]);
  };

  const handleCompanyCreated = (newCompany: any) => {
    const pill: CompanyPill = {
      id: newCompany.id, // Now always provided by AddCompanyForm after backend creation
      name: newCompany.name,
      domain: newCompany.website?.replace(/^https?:\/\//, ''),
      logoUrl: null,
    };
    setShowAddDialog(false);
    // Prompt for relationship type before committing
    setPendingCompany(pill);
    setSearching(true);
  };

  return (
    <>
      <div className="flex items-start gap-3 text-sm" ref={containerRef}>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            {associatedCompanies.map((company) => (
              <span
                key={company.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground group/pill"
              >
                <CompanyLogo name={company.name} domain={company.domain} size="sm" />
                {onCompanyClick ? (
                  <button
                    onClick={() => onCompanyClick(company)}
                    className="hover:text-accent transition truncate max-w-[120px]"
                  >
                    {company.name}
                  </button>
                ) : (
                  <span className="truncate max-w-[120px]">{company.name}</span>
                )}
                {company.relationship && (
                  <span className="rounded-full bg-background border border-border px-1.5 py-0 text-[10px] font-medium capitalize text-muted-foreground">
                    {relationshipLabel(company.relationship)}
                  </span>
                )}
                {canMutate && (
                  <button
                    onClick={() => removeCompany(company.id)}
                    className="opacity-0 group-hover/pill:opacity-100 transition hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
            {pendingCompany ? (
              <div className="relative inline-flex items-center gap-2 rounded-md border border-border bg-popover px-2 py-1 text-xs">
                <CompanyLogo name={pendingCompany.name} domain={pendingCompany.domain} size="sm" />
                <span className="truncate max-w-[120px] font-medium">{pendingCompany.name}</span>
                <span className="text-muted-foreground">{t("grid.association.as")}</span>
                {RELATIONSHIPS.map((rel) => (
                  <button
                    key={rel}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      confirmRelationship(rel);
                    }}
                    className="rounded-full border border-border px-2 py-0.5 text-[11px] capitalize hover:bg-accent hover:text-accent-foreground transition"
                  >
                    {relationshipLabel(rel)}
                  </button>
                ))}
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setPendingCompany(null);
                  }}
                  className="ml-1 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : searching ? (
              <div className="relative flex-1 min-w-[140px]">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setSearching(false);
                      setSearchText("");
                      setResults([]);
                    }
                  }}
                  placeholder={t("grid.cell.searchCompanies")}
                  className="w-full bg-transparent border-b border-accent outline-none text-xs py-0.5 px-0"
                />
                {loading && (
                  <div className="absolute right-0 top-0.5">
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  </div>
                )}
                {(results.length > 0 || searchText.trim()) && (
                  <div className="absolute top-full left-0 mt-1 z-50 w-max min-w-[240px] max-w-[420px] rounded-md border border-border bg-popover shadow-md py-1 max-h-48 overflow-y-auto">
                    {results.map((company) => (
                      <button
                        key={company.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          pickCompany(company);
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 transition flex items-center gap-2 whitespace-nowrap"
                      >
                        <CompanyLogo name={company.name} domain={company.domain} size="sm" />
                        <span className="flex-1">{company.name}</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCreateNew();
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 transition flex items-center gap-2 border-t border-border text-accent whitespace-nowrap"
                    >
                      <Plus className="h-3 w-3" />
                      <span>{searchText.trim() ? t("grid.cell.createNewCompanyNamed", { name: searchText.trim() }) : t("grid.cell.createNewCompany")}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : canMutate ? (
              <button
                onClick={() => setSearching(true)}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition"
              >
                <Plus className="h-3 w-3" />
                {associatedCompanies.length === 0 && t("grid.association.add")}
              </button>
            ) : associatedCompanies.length === 0 ? (
              <span className="text-xs text-muted-foreground">-</span>
            ) : null}
          </div>
        </div>
      </div>
      <AddCompanyDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onAdd={handleCompanyCreated}
      />
    </>
  );
}
